import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import { MoveFocusInside } from 'react-focus-lock';

import Accordion from '../../components/Accordion';
import Grid from '../../components/Grid';

import './styles.css';
import Divider from '../../components/Divider';
import AccordionTitle from './accordionTitle';
import AwardAmount from './awardAmount';
import Audience from './audience';
import ButtonSet from './buttonSet';
import Requirements from './requirements';
import Renewable from './renewable';
import ScholarshipName from './scholarshipName';
import { requiredValuesExist, checkForErrors, isEmptyObject, getCancelModal, getDeleteModal, getUnsavedModal, addCommas } from './utils';
import messages from './messages';

class ScholarshipForm extends React.PureComponent {
  static propTypes = {
    scholarship: PropTypes.shape({
      name: PropTypes.string,
      awardType: PropTypes.string,
      awAmountMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      awAmountMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      renewable: PropTypes.bool,
      audience: PropTypes.string,
      audienceStates: PropTypes.array,
      gpaMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      gpaMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      classRankMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      classRankMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      satMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      satMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      actMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      actMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      gpaRankMatch: PropTypes.string,
      satGpaMatch: PropTypes.string,
      satActMatch: PropTypes.string,
    }),
    onCancel: PropTypes.func,
    onDelete: PropTypes.func,
    onSave: PropTypes.func,
    toggleForm: PropTypes.func,
    onChange: PropTypes.func,
    checkForDuplicateNames: PropTypes.func,
    config: PropTypes.shape({
      index: PropTypes.string,
      formOpen: PropTypes.bool,
      formDirty: PropTypes.bool,
      errors: PropTypes.object,
      forceShowErrors: PropTypes.bool,
      isNew: PropTypes.bool,
    }),
    isFocus: PropTypes.bool,
    intl: PropTypes.object,
  };

  constructor(props) {
    super(props);
    const { scholarship } = props;
    if (scholarship && isNil(scholarship.audienceStates)) scholarship.audienceStates = [];
    this.state = {
      deleting: false,
      openDeleteModal: false,
      openCancelModal: false,
      openUnsavedModal: false,
    };
  }

  checkAllRequiredFieldsPresent = ({ gpaMin, classRankMax, satMin, actMin, audience, audienceStates }) =>
    (audience !== 'CUSTOM' || (audienceStates && audienceStates.length > 0)) &&
      requiredValuesExist({ gpaMin, classRankMax, satMin, actMin });

  toggleForm = () => {
    const { config } = this.props;
    if (config.formOpen) {
      if (config.formDirty) {
        this.setState({ openUnsavedModal: true });
      } else /* istanbul ignore else */ if (!config.forceShowErrors) {
        this.onConfirmCancel();
      }
    } else {
      this.props.toggleForm(config.index);
    }
  };

  onChange = (e, fieldName) => {
    const { config, onChange } = this.props;
    const value = get(e, 'target.value');
    onChange(config.index, fieldName, value);
  };

  onDelete = (event) => {
    event.stopPropagation();
    this.setState({ openDeleteModal: true });
  };

  onCancelDelete = (e) => {
    this.setState({ openDeleteModal: false });
  };

  onConfirmDelete = (e) => {
    this.setState({ openDeleteModal: false, deleting: true });
    const { onDelete, config } = this.props;
    onDelete(config.index).then(() => {
      this.setState({ deleting: false });
    }).catch(/* istanbul ignore next */ () => {
      this.setState({ deleting: false });
    });
  };

  onEdit = (event, { value }) => {
    event.stopPropagation();
    const { toggleForm, config } = this.props;
    toggleForm(config.index);
  };

  onCancel = (event) => {
    event.stopPropagation();
    const { onCancel, config } = this.props;
    if (config.formDirty) {
      this.setState({ openCancelModal: true });
    } else {
      onCancel(config.index);
    }
  };

  onCancelCancel = (e) => {
    this.setState({ openCancelModal: false });
  };

  onConfirmCancel = (e) => {
    const { onCancel, config } = this.props;
    this.setState({
      openCancelModal: false,
    });
    onCancel(config.index);
  };

  onUnsavedCancel = (e) => {
    this.setState({
      openUnsavedModal: false,
    });
    this.onConfirmCancel(e);
  }

  onConfirmSave = async (e) => {
    await this.onSave();
    this.setState({ openUnsavedModal: false });
  }

  onSave = async () => {
    const { config, onSave } = this.props;
    this.setState({ saving: true });
    return onSave(config.index).then(() => this.setState({ saving: false }))
      .catch(/* istanbul ignore next */ () => this.setState({ saving: false }));
  };

  render() {
    const { config, scholarship, isFocus, checkForDuplicateNames, intl } = this.props;
    const { openCancelModal, openDeleteModal, openUnsavedModal, deleting, saving } = this.state;
    const {
      name,
      awardType,
      awAmountMin,
      awAmountMax,
      renewable,
      audience,
      audienceStates,
      gpaMin,
      gpaMax,
      classRankMin,
      classRankMax,
      satMin,
      satMax,
      actMin,
      actMax,
      gpaRankMatch,
      satGpaMatch,
      satActMatch,
    } = scholarship;
    const { index, forceShowErrors, errors: stateErrors, formOpen, formDirty } = config;

    const initialScholarshipName = name === '' ? 'Untitled Scholarship' : name;
    let errors = isEmptyObject(omit(stateErrors, 'requiredFieldsMissing')) ? checkForErrors(intl, scholarship) : stateErrors;
    errors = formDirty || forceShowErrors ? errors : { requiredFieldsMissing: [] };
    const allRequirementsMet = this.checkAllRequiredFieldsPresent(scholarship);
    if (formDirty && checkForDuplicateNames(index, name)) {
      errors.duplicateName = intl.formatMessage(messages.duplicateName);
    }

    return (
      <React.Fragment>
        <Accordion.Title active={formOpen} index={index} onClick={this.toggleForm} styleName="accordion-title">
          <AccordionTitle
            index={index}
            open={formOpen}
            scholarshipName={initialScholarshipName}
            awardType={awardType}
            awAmountMin={awAmountMin}
            awAmountMax={awAmountMax}
            renewable={renewable}
            onEdit={this.onEdit}
            onDelete={this.onDelete}
            deleting={deleting}
          />
        </Accordion.Title>
        {(formOpen && !deleting) && (
          <Accordion.Content active={formOpen}>
            <MoveFocusInside disabled={!isFocus}>
              <Grid stackable width={10} styleName="merit-scholarships-general-info-grid">
                <ScholarshipName index={index} name={name} onChange={this.onChange} errors={errors} forceShowErrors={forceShowErrors} />
                <AwardAmount
                  index={index}
                  awardType={awardType}
                  awAmountMin={addCommas(awAmountMin)}
                  awAmountMax={addCommas(awAmountMax)}
                  onChange={this.onChange}
                  errors={errors}
                  intl={intl}
                  forceShowErrors={forceShowErrors}
                />
                <Renewable index={index} awardType={awardType} renewable={renewable} onChange={this.onChange} />
                <Audience index={index} audience={audience} audienceStates={audienceStates} onChange={this.onChange} errors={errors} />
                <Requirements
                  index={index}
                  gpaMin={gpaMin}
                  gpaMax={gpaMax}
                  gpaRankMatch={gpaRankMatch}
                  classRankMax={classRankMax}
                  classRankMin={classRankMin}
                  satGpaMatch={satGpaMatch}
                  satMin={satMin}
                  satMax={satMax}
                  satActMatch={satActMatch}
                  actMin={actMin}
                  actMax={actMax}
                  onChange={this.onChange}
                  errors={errors}
                  forceShowErrors={forceShowErrors}
                />
                <ButtonSet
                  index={index}
                  onSave={this.onSave}
                  onCancel={this.onCancel}
                  onDelete={this.onDelete}
                  errors={errors}
                  canSave={allRequirementsMet}
                  saving={saving}
                />
              </Grid>
            </MoveFocusInside>
          </Accordion.Content>
        )}
        <Divider styleName="menu-divider" />
        {getUnsavedModal(this.props.intl, openUnsavedModal, this.onUnsavedCancel, this.onConfirmSave)}
        {getCancelModal(this.props.intl, openCancelModal, this.onCancelCancel, this.onConfirmCancel)}
        {getDeleteModal(this.props.intl, openDeleteModal, this.onCancelDelete, this.onConfirmDelete)}
      </React.Fragment>
    );
  }
}

export default injectIntl(ScholarshipForm);
